import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Send, ArrowLeft } from "lucide-react";

export default function TicketDetail() {
  const { ticketId } = useParams();
  const { user } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState("");

  useEffect(() => {
    fetchTicket();
  }, [ticketId]);

  const fetchTicket = async () => {
    try {
      const res = await api.get(`/tickets/${ticketId}`);
      setTicket(res.data);
    } catch (err) {
      console.error("Ticket detail error:", err);
    } finally {
      setLoading(false);
    }
  };

  const sendReply = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/tickets/${ticketId}/replies`, { message: reply });
      setReply("");
      fetchTicket();
    } catch (err) {
      alert("Fehler beim Senden der Antwort: " + err.response?.data?.detail);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Laden...</div>;
  }

  if (!ticket) {
    return <div className="min-h-screen flex items-center justify-center">Ticket nicht gefunden</div>;
  }

  return (
    <div className="min-h-screen bg-[#07090F] text-slate-100 selection:bg-[#E63946] selection:text-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <Button 
            variant="ghost" 
            onClick={() => window.history.back()}
            className="text-slate-400 hover:text-white hover:bg-white/5 border border-white/10"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Zurück
          </Button>
        </div>

        <Card className="mb-6 bg-[#0F1422] border-white/10 text-white shadow-xl">
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <CardTitle className="text-xl font-bold text-white tracking-tight">{ticket.subject}</CardTitle>
              <div className="flex gap-2">
                <Badge variant={
                  ticket.status === 'open' ? 'destructive' :
                  ticket.status === 'in_progress' ? 'default' :
                  ticket.status === 'answered' ? 'secondary' : 'outline'
                } className={
                  ticket.status === 'open' ? 'bg-[#E63946] text-white border-0' :
                  ticket.status === 'in_progress' ? 'bg-[#FF7A00] text-white border-0' :
                  ticket.status === 'answered' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 
                  'bg-white/10 text-slate-400 border-white/10'
                }>
                  {ticket.status === 'open' ? 'Offen' :
                   ticket.status === 'in_progress' ? 'In Bearbeitung' :
                   ticket.status === 'answered' ? 'Beantwortet' : 'Geschlossen'}
                </Badge>
                <Badge className={
                  ticket.priority === 'high' ? 'bg-red-500/20 text-red-400 border border-red-500/30' :
                  ticket.priority === 'medium' ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' : 
                  'bg-slate-500/20 text-slate-400 border border-slate-500/30'
                }>
                  {ticket.priority === 'high' ? 'Hoch' :
                   ticket.priority === 'medium' ? 'Normal' : 'Niedrig'}
                </Badge>
              </div>
            </div>
            <div className="text-xs text-slate-400 mt-2">
              Ticket #{ticket.id} • {ticket.category} • 
              Erstellt am {new Date(ticket.createdAt).toLocaleDateString('de-DE')}
            </div>
          </CardHeader>
        </Card>

        <div className="space-y-4 mb-6">
          {/* Original message */}
          <Card className="bg-[#0F1422] border-white/10 text-white shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-[#E63946] to-[#b31b27] rounded-xl flex items-center justify-center text-white font-bold text-sm shadow-md">
                  {user?.firstName?.[0] || 'K'}{user?.lastName?.[0] || 'U'}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-semibold text-white">{user?.firstName} {user?.lastName}</span>
                    <span className="text-xs text-slate-400">
                      {new Date(ticket.createdAt).toLocaleString('de-DE')}
                    </span>
                  </div>
                  <div className="text-slate-300 whitespace-pre-wrap leading-relaxed">{ticket.message}</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Replies */}
          {ticket.replies && ticket.replies.map((r) => (
            <Card key={r.id} className="bg-[#0F1422] border-white/10 text-white shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-sm shadow-md ${
                    r.userId ? 'bg-gradient-to-br from-[#E63946] to-[#b31b27]' : 'bg-gradient-to-br from-emerald-600 to-teal-700'
                  }`}>
                    {r.userName ? r.userName[0] : 'S'}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-semibold text-white">
                        {r.userName || 'RedWORK Support'}
                      </span>
                      <span className="text-xs text-slate-400">
                        {new Date(r.createdAt).toLocaleString('de-DE')}
                      </span>
                    </div>
                    <div className="text-slate-300 whitespace-pre-wrap leading-relaxed">{r.message}</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {ticket.status !== 'closed' && (
          <Card className="bg-[#0F1422] border-white/10 text-white shadow-xl">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-white">Antwort hinzufügen</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={sendReply}>
                <Textarea
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder="Ihre Antwort eingeben..."
                  rows={4}
                  required
                  className="mb-4 bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:border-[#FF7A00] focus:ring-1 focus:ring-[#FF7A00]"
                />
                <Button type="submit" className="bg-[#E63946] hover:bg-[#d52b38] text-white font-semibold shadow-lg shadow-red-500/20">
                  <Send className="h-4 w-4 mr-2" />
                  Antwort senden
                </Button>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}